import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import styles from './index.pcss';
import FAQAccordion from './FAQAccordian/FaqAccordion';
import { getGSTODPConfig } from '../../../../../refresh/selectors/featureFlagsSelector';
import { segmentEventPush } from '../../../../../modules/ga/GTMdatalayer';

const cx = classNames.bind(styles);
let hasTrackedMoreInfoScreen = false;

// Enhanced (GST 3.0) billing / shipping detail card for the More Info side modal.
const GstDetailCard = ({ label, addr = {}, textConfig = {} }) => {
  const {
    name = '',
    address = '',
    phone = '',
    gstin = '',
    gstApplicable = true,
    gstExpired = false
  } = addr;
  return (
    <div className={cx('gst-detail-card')}>
      <div className={cx('gst-detail-label')}>{label}</div>
      <div className={cx('gst-detail-name-row')}>
        <span className={cx('gst-detail-name')}>{name}</span>
        {gstApplicable ? (
          <span className={cx('gst-detail-badge')}>{textConfig.addressGstLabel}</span>
        ) : (
          <span className={cx('gst-detail-badge', 'gst-detail-badge--na')}>
            {textConfig.gstNonApplicableLabel}
          </span>
        )}
        {gstApplicable && gstExpired ? (
          <span className={cx('gst-detail-badge', 'gst-detail-badge--expired')}>
            {textConfig.gstExpiredLabel || 'EXPIRED'}
          </span>
        ) : null}
      </div>
      <div className={cx('gst-detail-address')}>{address}</div>
      {phone ? <div className={cx('gst-detail-phone')}>Phone Number: +91 {phone}</div> : null}
      {gstApplicable && gstin ? (
        <div className={cx('gst-detail-gstin')}>
          {textConfig.gstinLabel} <span>{gstin}</span>
        </div>
      ) : null}
      {!gstApplicable && textConfig.shippingGstNote ? (
        <div className={cx('gst-detail-note')}>{textConfig.shippingGstNote}</div>
      ) : null}
    </div>
  );
};

const getAccordionItems = faq2 => {
  if (!Array.isArray(faq2) || faq2.length === 0) {
    return [
      { title: 'GST Related Queries', questionAnswer: [] },
      { title: 'GST & EMI Related Queries', questionAnswer: [] },
      { title: 'GST & Bank Discount Queries', questionAnswer: [] },
      { title: 'GST, EMI & Bank Discount Queries', questionAnswer: [] }
    ];
  }

  if (faq2[0]?.title || faq2[0]?.faqTopic) {
    return faq2.map(item => ({
      ...item,
      title: item?.title || item?.faqTopic || '',
      questionAnswer:
        item?.questionAnswer || item?.questionAnswers || item?.questionAnswerList || []
    }));
  }

  return [
    { title: 'GST Related Queries', questionAnswer: faq2 },
    { title: 'GST & EMI Related Queries', questionAnswer: [] },
    { title: 'GST & Bank Discount Queries', questionAnswer: [] },
    { title: 'GST, EMI & Bank Discount Queries', questionAnswer: [] }
  ];
};

const GstInfoSideModal = ({
  gstNumber,
  businessName,
  billingAddressLabel = 'Billing Address:',
  gstinLabel = 'GSTIN:',
  address,
  handleDownloadInvoiceClick,
  handleAccordionClick,
  isDeliveredEntriesEmpty,
  isThankYouPage = false,
  odpConfig,
  orderData = {},
  moreInfoTrackingPayload = {},
  luxe,
  enableGstEnhancement = false,
  gstTextConfig = {},
  billingAddress = {},
  shippingAddress = {}
}) => {
  const faqData = (enableGstEnhancement && gstTextConfig?.faqs2) || odpConfig?.faqs2 || [];
  const gstDetailsTitle = enableGstEnhancement
    ? gstTextConfig?.gstDetailsTitle || odpConfig?.gstDetailsTitle
    : odpConfig?.gstDetailsTitle;
  const faqTitle = enableGstEnhancement
    ? gstTextConfig?.faqTitle || odpConfig?.faqTitle
    : odpConfig?.faqTitle;

  useEffect(() => {
    if (hasTrackedMoreInfoScreen) return;
    hasTrackedMoreInfoScreen = true;
    segmentEventPush({
      event: 'GST More Info Screen Viewed',
      screen_name: 'GST More Info Screen',
      ...moreInfoTrackingPayload
    });
    return () => {
      hasTrackedMoreInfoScreen = false;
    };
  }, []);

  const filteredFaqs = useMemo(() => {
    if (!Array.isArray(faqData)) return [];
    if (!faqData[0]?.key) return faqData;

    const { emiOfferDetails = {}, isClawBackEnabled = false } = orderData || {};
    const { noCostEmiDiscount = 0 } = emiOfferDetails || {};

    return faqData.filter(item => {
      switch (item?.key) {
        case 'gst':
          return true;
        case 'emi':
          return noCostEmiDiscount > 0;
        case 'bankDiscount':
          return isClawBackEnabled;
        case 'all':
          return noCostEmiDiscount > 0 && isClawBackEnabled;
        default:
          return true;
      }
    });
  }, [faqData, orderData]);

  const accordionItems = useMemo(() => getAccordionItems(filteredFaqs), [filteredFaqs]);

  return (
    <div
      className={cx('side-modal-container', {
        'luxe-side-modal-container': luxe,
        'is-thankyou': isThankYouPage
      })}
    >
      <div className={cx('modal-header')}>{odpConfig?.moreInfoTitle}</div>
      <div className={cx('modal-body')}>
        <h3 className={cx('gst-details-title')}>{gstDetailsTitle}</h3>
        {enableGstEnhancement ? (
          <div className={cx('gst-detail-cards')}>
            <GstDetailCard
              label={gstTextConfig?.billingDetailsLabel}
              addr={billingAddress}
              textConfig={gstTextConfig}
            />
            <GstDetailCard
              label={gstTextConfig?.shippingDetailsLabel}
              addr={shippingAddress}
              textConfig={gstTextConfig}
            />
          </div>
        ) : (
          <div className={cx('gst-box')}>
            <div className={cx('company-name')}>{businessName}</div>
            <div className={cx('billing-address')}>
              <div>{billingAddressLabel}</div>
              {address}
            </div>
              <div className={cx('gstin')}>
                <span aria-hidden="true">{gstinLabel}</span><span className="sr-only">
                  {gstinLabel.split('').join(', ')}
                </span> <span><span aria-hidden="true">{gstNumber}</span><span className="sr-only">
                  {gstNumber?.split('').join(', ')}
                </span></span>
              </div>
          </div>
        )}

        <h3 className={cx('faq-title')}>{faqTitle}</h3>
        <FAQAccordion
          accordionItems={accordionItems}
          isDeliveredEntriesEmpty={isDeliveredEntriesEmpty}
          isThankYouPage={isThankYouPage}
          handleDownloadInvoiceClick={handleDownloadInvoiceClick}
          handleAccordionClick={handleAccordionClick}
          luxe={luxe}
        />
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  odpConfig: getGSTODPConfig(state)
});

export { GstInfoSideModal };
export default connect(mapStateToProps)(GstInfoSideModal);
